import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, getDocs, updateDoc, doc, arrayUnion, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../Config/Config';

const Withdraw = () => {

    const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/');
        return;
      }

      try {
        const adminDocRef = doc(db, 'admins', user.uid);
        const adminDoc = await getDoc(adminDocRef);
        
        if (!adminDoc.exists() || adminDoc.data().role !== 'admin') {
          navigate('/');
        }
      } catch (error) {
        console.error('Admin verification failed:', error);
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);



    const [withdraws, setWithdraws] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState({});

    useEffect(() => {
        const fetchWithdraws = async () => {
            try {
                const withdrawsCollection = collection(db, 'withdraws');
                const q = query(withdrawsCollection);
                const querySnapshot = await getDocs(q);
                
                const withdrawsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    firstName: doc.data().firstName || '',
                    lastName: doc.data().lastName || '',
                    status: doc.data().status || 'pending',
                    Totalwithdraw: doc.data().Totalwithdraw || 0,
                    amountWithdraw: doc.data().amountWithdraw || 0,
                    walletAddress: doc.data().WalletAddress || '',
                    uid: doc.data().uid || '',
                }));

                setWithdraws(withdrawsData);
            } catch (error) {
                console.error("Error fetching withdraws: ", error);
            } finally {
                setLoading(false);
            }
        };

        fetchWithdraws();
    }, []);

    const handleEditClick = (id) => {
        setEditMode(prev => ({ ...prev, [id]: true }));
    };

    const handleStatusChange = (id, newStatus) => {
        setWithdraws(prevWithdraws => 
            prevWithdraws.map(withdraw => 
                withdraw.id === id ? { ...withdraw, status: newStatus } : withdraw
            )
        );
    };

    const handleSubmit = async (id) => {
        try {
            const withdrawToUpdate = withdraws.find(withdraw => withdraw.id === id);
            const { status, Totalwithdraw, amountWithdraw, uid } = withdrawToUpdate;

            if (status === "approved") {
                const newTotalwithdraw = Number(Totalwithdraw) + Number(amountWithdraw);

                const transHistoryRef = doc(db, 'TransHistory', uid);
                await setDoc(transHistoryRef, {
                    withdrawlTrans: arrayUnion({
                        date: new Date().toISOString(),
                        amount: Number(amountWithdraw),
                        walletAddress: withdrawToUpdate.walletAddress,
                    })
                }, { merge: true });

                const withdrawRef = doc(db, 'withdraws', id);
                await updateDoc(withdrawRef, {
                    Totalwithdraw: newTotalwithdraw,
                    amountWithdraw: 0,
                    status: status
                });
            } else {
                const withdrawRef = doc(db, 'withdraws', id);
                await updateDoc(withdrawRef, {
                    status: status
                });
            }

            setEditMode(prev => ({ ...prev, [id]: false }));
        } catch (error) {
            console.error("Error updating status: ", error);
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading withdrawals...</div>;
    }

    if (withdraws.length === 0) {
        return <div className="text-center py-8 text-gray-500">No withdrawal requests found</div>;
    }

    return (
        <div className="p-4 md:p-6 min-h-screen">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">Withdrawal Requests</h1>
            
            {/* Mobile View (Cards) */}
            <div className="md:hidden space-y-4">
                {withdraws.map(withdraw => (
                    <div key={withdraw.id} className="bg-white p-4 rounded-lg shadow">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium">{withdraw.firstName} {withdraw.lastName}</h3>
                            <span className={`text-xs px-2 py-1 rounded ${
                                withdraw.status === 'approved' ? 'bg-green-100 text-green-800' :
                                withdraw.status === 'declined' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                            }`}>
                                {withdraw.status}
                            </span>
                        </div>
                        
                        <div className="text-sm space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Amount:</span>
                                <span>${withdraw.amountWithdraw.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Wallet:</span>
                                <span className="font-mono text-xs truncate max-w-[120px]" title={withdraw.walletAddress}>
                                    {withdraw.walletAddress}
                                </span>
                            </div>
                        </div>

                        {editMode[withdraw.id] ? (
                            <div className="mt-3 space-y-2">
                                <select 
                                    value={withdraw.status} 
                                    onChange={(e) => handleStatusChange(withdraw.id, e.target.value)}
                                    className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800"
                                >
                                    <option value="approved">Approved</option>
                                    <option value="declined">Declined</option>
                                    <option value="pending">Pending</option>
                                </select>
                                <button 
                                    onClick={() => handleSubmit(withdraw.id)}
                                    className="w-full bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 transition duration-200 text-sm"
                                >
                                    Submit
                                </button>
                            </div>
                        ) : (
                            <button 
                                onClick={() => handleEditClick(withdraw.id)}
                                className="w-full mt-3 bg-[#000300] text-white px-3 py-2 rounded hover:bg-gray-800 transition duration-200 text-sm"
                            >
                                Edit Status
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Tablet/Desktop View (Table) */}
            <div className="hidden md:block overflow-x-auto shadow-lg rounded-lg">
                <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                    <thead className="bg-gray-200">
                        <tr>
                            <th className="py-3 px-4 border-b border-gray-300 text-left text-gray-600">Name</th>
                            <th className="py-3 px-4 border-b border-gray-300 text-left text-gray-600">Wallet Address</th>
                            <th className="py-3 px-4 border-b border-gray-300 text-left text-gray-600">Amount</th>
                            <th className="py-3 px-4 border-b border-gray-300 text-left text-gray-600">Status</th>
                            <th className="py-3 px-4 border-b border-gray-300 text-left text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {withdraws.map(withdraw => (
                            <tr key={withdraw.id} className="hover:bg-gray-100 transition-colors duration-200">
                                <td className="py-3 px-4 border-b border-gray-300">{withdraw.firstName} {withdraw.lastName}</td>
                                <td className="py-3 px-4 border-b border-gray-300">
                                    <span className="font-mono text-sm bg-gray-100 p-1 rounded truncate max-w-[180px] inline-block" title={withdraw.walletAddress}>
                                        {withdraw.walletAddress}
                                    </span>
                                </td>
                                <td className="py-3 px-4 border-b border-gray-300">
                                    ${withdraw.amountWithdraw.toFixed(2)}
                                </td>
                                <td className="py-3 px-4 border-b border-gray-300">
                                    {editMode[withdraw.id] ? (
                                        <select 
                                            value={withdraw.status} 
                                            onChange={(e) => handleStatusChange(withdraw.id, e.target.value)}
                                            className="border rounded p-1 focus:outline-none focus:ring-2 focus:ring-gray-800"
                                        >
                                            <option value="approved">Approved</option>
                                            <option value="declined">Declined</option>
                                            <option value="pending">Pending</option>
                                        </select>
                                    ) : (
                                        <span className={`font-medium ${
                                            withdraw.status === 'approved' ? 'text-green-600' :
                                            withdraw.status === 'declined' ? 'text-red-600' :
                                            'text-yellow-600'
                                        }`}>
                                            {withdraw.status}
                                        </span>
                                    )}
                                </td>
                                <td className="py-3 px-4 border-b border-gray-300">
                                    {editMode[withdraw.id] ? (
                                        <button 
                                            onClick={() => handleSubmit(withdraw.id)}
                                            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition duration-200"
                                        >
                                            Submit
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => handleEditClick(withdraw.id)}
                                            className="bg-[#000300] text-white px-3 py-1 rounded hover:bg-gray-800 transition duration-200"
                                        >
                                            Edit
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Withdraw;